import * as THREE from 'three';
import { useMemo } from 'react';

function useCupCausticsMaterial() {
  const material = useMemo(() => new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uQuality: { value: 1.0 }, // Quality control for adaptive performance
    },
    vertexShader: /*glsl*/`
      varying vec2 vUv;

      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /*glsl*/`
      #ifdef GL_ES
      precision mediump float; // Use medium precision for better performance
      #endif
      
      uniform float uTime;
      uniform float uQuality;

      #define TAU 6.28318530718
      #define SCALE 0.8

      varying vec2 vUv;

      void main() {
        float time = uTime * 0.6+23.0;
        vec2 uv = vUv * SCALE;
        vec2 p = mod(uv*TAU, TAU)-250.0;
        vec2 i = vec2(p);
        float c = 1.0;
        float inten = .004;

        // Fixed iteration count for better performance
        int maxIter = 3;
        
        // Unroll the loop for better performance
        // Iteration 1
        float t = time * (1.0 - (3.5 / 1.0));
        i = p + vec2(cos(t - i.x) + sin(t + i.y), sin(t - i.y) + cos(t + i.x));
        c += 1.0/length(vec2(p.x / (sin(i.x+t)/inten),p.y / (cos(i.y+t)/inten)));
        
        // Iteration 2
        t = time * (1.0 - (3.5 / 2.0));
        i = p + vec2(cos(t - i.x) + sin(t + i.y), sin(t - i.y) + cos(t + i.x));
        c += 1.0/length(vec2(p.x / (sin(i.x+t)/inten),p.y / (cos(i.y+t)/inten)));
        
        // Iteration 3
        t = time * (1.0 - (3.5 / 3.0));
        i = p + vec2(cos(t - i.x) + sin(t + i.y), sin(t - i.y) + cos(t + i.x));
        c += 1.0/length(vec2(p.x / (sin(i.x+t)/inten),p.y / (cos(i.y+t)/inten)));
        
        c /= float(maxIter);
        c = 1.17-pow(c, 1.4);
        
        // Simplified color calculation
        vec3 colour = vec3(pow(abs(c), 6.0));
        colour = clamp(colour + vec3(0.0, 0.3, 0.6), 0.0, 1.0);

        gl_FragColor = vec4(colour, 0.8);
      }
    `,
    transparent: true,
    depthWrite: true, // Enable depth writing for better rendering
    depthTest: true,  // Enable depth testing for better rendering
    side: THREE.DoubleSide, // Render both sides
    blending: THREE.NormalBlending,
    dithering: true, // Enable dithering for smoother transitions
  }), []);
  
  return material;
}

export default useCupCausticsMaterial;
